/* Alakhyaar design system - small, dependency-free behaviours.
   1. Reveal-on-scroll for .ak-reveal
   2. Hero banner slider: [data-ak-slider]
      - swipe / scroll-snap on touch, arrows + dots on desktop
      - optional autoplay that follows Baymard's rules: desktop only, slow,
        pauses on hover/focus, and stops for good after any interaction
   3. Product tabs: [data-ak-tabs]
   Everything degrades to a plain, fully visible page if this file fails. */
(function () {
  'use strict';

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- reveal ---------- */
  function reveal() {
    var items = [].slice.call(document.querySelectorAll('.ak-reveal'));
    if (!items.length) return;
    var showAll = function () { items.forEach(function (el) { el.classList.add('is-in'); }); };
    if (reduce || !('IntersectionObserver' in window)) { showAll(); return; }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    items.forEach(function (el) { io.observe(el); });
    window.setTimeout(showAll, 6000); // never leave content hidden
  }

  /* ---------- hero slider ---------- */
  function slider(root) {
    var track = root.querySelector('[data-ak-slides]');
    if (!track) return;
    var slides = [].slice.call(track.children);
    if (slides.length < 2) return;

    var dots = [].slice.call(root.querySelectorAll('[data-ak-dot]'));
    var prev = root.querySelector('[data-ak-prev]');
    var next = root.querySelector('[data-ak-next]');
    var index = 0;
    var stopped = false;
    var paused = false;
    var timer = null;

    function mark(i) {
      index = i;
      slides.forEach(function (s, k) { s.classList.toggle('is-active', k === i); });
      dots.forEach(function (d, k) { d.setAttribute('aria-current', k === i ? 'true' : 'false'); });
    }

    function go(i, byUser) {
      i = (i + slides.length) % slides.length;
      track.scrollTo({ left: slides[i].offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
      mark(i);
      if (byUser) stop();
    }

    function stop() { stopped = true; clearInterval(timer); timer = null; }

    var ticking = false;
    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        var i = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
        if (i !== index) mark(i);
        ticking = false;
      });
    }, { passive: true });

    if (prev) prev.addEventListener('click', function () { go(index - 1, true); });
    if (next) next.addEventListener('click', function () { go(index + 1, true); });
    dots.forEach(function (d, k) { d.addEventListener('click', function () { go(k, true); }); });

    // any manual gesture ends autoplay for good
    ['pointerdown', 'touchstart', 'keydown', 'wheel'].forEach(function (ev) {
      root.addEventListener(ev, function () { if (ev !== 'wheel' || Math.abs(arguments[0].deltaX) > 2) stop(); }, { passive: true });
    });

    // autoplay: opt-in, desktop with a pointer only, never with reduced motion
    var wantsAuto = root.getAttribute('data-autoplay') === 'true';
    var canAuto = window.matchMedia && window.matchMedia('(hover: hover) and (min-width: 901px)').matches;
    if (wantsAuto && canAuto && !reduce) {
      var delay = (parseFloat(root.getAttribute('data-speed')) || 7) * 1000;
      var start = function () {
        if (stopped || timer) return;
        timer = setInterval(function () { if (!paused && !document.hidden) go(index + 1, false); }, delay);
      };
      root.addEventListener('mouseenter', function () { paused = true; });
      root.addEventListener('mouseleave', function () { paused = false; });
      root.addEventListener('focusin', function () { paused = true; });
      root.addEventListener('focusout', function () { paused = false; });
      start();
    }

    mark(0);
  }

  /* ---------- product tabs ---------- */
  function tabs(root) {
    var buttons = [].slice.call(root.querySelectorAll('[role="tab"]'));
    var cells = [].slice.call(root.querySelectorAll('[data-tab]'));
    var empty = root.querySelector('.ak-pempty');
    if (!buttons.length) return;

    function select(filter) {
      buttons.forEach(function (b) {
        var on = b.getAttribute('data-filter') === filter;
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
        if (on) { // on narrow screens the tab row scrolls sideways: keep the chosen tab in view
          var bar = b.parentNode;
          if (bar.scrollWidth > bar.clientWidth) {
            var br = b.getBoundingClientRect(), pr = bar.getBoundingClientRect();
            bar.scrollTo({ left: bar.scrollLeft + (br.left - pr.left) - (pr.width - br.width) / 2, behavior: 'smooth' });
          }
        }
      });
      var shown = 0;
      cells.forEach(function (c) {
        // data-tab holds the handles of the tab collections the product belongs to, separated by spaces
        var on = filter === 'all' || (' ' + c.getAttribute('data-tab') + ' ').indexOf(' ' + filter + ' ') > -1;
        c.hidden = !on;
        if (on) shown++;
      });
      if (empty) empty.hidden = shown > 0;
    }

    buttons.forEach(function (b, i) {
      b.addEventListener('click', function () { select(b.getAttribute('data-filter')); });
      b.addEventListener('keydown', function (e) {
        var t = null;
        if (e.key === 'ArrowRight') t = buttons[(i + 1) % buttons.length];
        else if (e.key === 'ArrowLeft') t = buttons[(i - 1 + buttons.length) % buttons.length];
        if (t) { e.preventDefault(); t.focus(); select(t.getAttribute('data-filter')); }
      });
    });
  }

  /* ---------- cart count in the header bubble ---------- */
  // The theme fires "updateCart.ajaxcart" (with the /cart.js data) after every AJAX cart change; it only toggles the bubble itself.
  function cartCount() {
    var bubbles = [].slice.call(document.querySelectorAll('[data-ak-cart-count]'));
    if (!bubbles.length || !window.jQuery) return;
    window.jQuery('body').on('updateCart updateCart.ajaxcart', function (e, cart) {
      if (!cart || typeof cart.item_count !== 'number') return;
      bubbles.forEach(function (b) { b.textContent = cart.item_count; });
    });
  }

  /* ---------- dialogs: <button data-ak-dialog-open="id"> opens <dialog id="id">; a close button or a click on the backdrop closes it ---------- */
  function dialogs() {
    document.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-ak-dialog-open]');
      if (opener) {
        var d = document.getElementById(opener.getAttribute('data-ak-dialog-open'));
        if (d) { if (d.showModal) d.showModal(); else d.setAttribute('open', ''); }
        return;
      }
      var closer = e.target.closest('[data-ak-dialog-close]');
      if (closer) { var own = closer.closest('dialog'); if (own) { if (own.close) own.close(); else own.removeAttribute('open'); } return; }
      if (e.target.tagName === 'DIALOG' && e.target.close) e.target.close();
    });
  }

  /* ---------- cart page: changing a quantity updates the cart, no extra click on "Update cart" ---------- */
  function cartPage() {
    var form = document.querySelector('form.ak-cart');
    if (!form) return;
    var timer;
    form.addEventListener('change', function (e) {
      if (!e.target.classList.contains('cart__product-qty')) return;
      clearTimeout(timer);
      timer = setTimeout(function () {
        var btn = form.querySelector('[name="update"]');
        if (form.requestSubmit) form.requestSubmit(btn); else if (btn) btn.click();
      }, 600);
    });
  }

  function init() {
    document.documentElement.classList.add('ak-ready'); // tells the CSS failsafe that JS is in charge
    cartCount();
    dialogs();
    cartPage();
    reveal();
    [].forEach.call(document.querySelectorAll('[data-ak-slider]'), slider);
    [].forEach.call(document.querySelectorAll('[data-ak-tabs]'), tabs);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
